import { ref, computed, type ComputedRef, type InjectionKey } from 'vue';
import { provideLocal, injectLocal } from '@vueuse/core';
import { useStickyAtbSlideoutContextEnabled } from './useStickyAtbSlideoutContextEnabled';
import { useProductType } from '~/composables/useProductType';

const TRAITS_ERROR_ACTIVE: InjectionKey<{ errorTextActive: ComputedRef<boolean> }> = Symbol('TRAITS_ERROR_ACTIVE');

/**
 * @description Provides whether the traits error text is currently active in the ancestor context.
 */
export function useTraitsErrorActiveProvide() {
  const { isStickyAtbSlideoutContextEnabled } = useStickyAtbSlideoutContextEnabled();
  const { pageLevelFeatureFlags: { isStickyAtbEnabled = false } = {} } = useProductType();

  const isErrorActive = ref<boolean>(false);

  const errorTextActive = computed<boolean>({
    get() {
      return !isStickyAtbSlideoutContextEnabled && isStickyAtbEnabled && isErrorActive.value;
    },
    set(newValue) {
      isErrorActive.value = newValue;
    },
  });

  provideLocal(TRAITS_ERROR_ACTIVE, { errorTextActive });
}

/**
 * @description Retrieves whether the traits error text is active in the nearest ancestor.
 * @returns {object} { errorTextActive: ComputedRef<boolean> } True if the traits error is active, false otherwise.
 */
export function useTraitsErrorActive() {
  const context = injectLocal(TRAITS_ERROR_ACTIVE);
  if (!context) {
    throw new Error('`useTraitsErrorActive` must be used within a component that has `useTraitsErrorActiveProvide` as an ancestor.');
  }
  return context;
}
